import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate, useRouterState } from '@tanstack/react-router';
import { useSwipeDown } from '../../hooks/useSwipeDown.js';
import { useAuth } from '../../features/auth/useAuth.js';
import { useLiveData } from '../../features/live/live-context.js';
import { useMode } from '../../contexts/ModeContext.js';
import { VoiceAssistant } from '../../features/voice/VoiceAssistant.js';
import { ConnectionStatus } from '../ui/ConnectionStatus.js';

const BASE_TABS = [
  { label: 'Overview', to: '/dashboard' },
  { label: 'Tooling', to: '/tooling' },
  { label: 'Schedule', to: '/schedule' },
] as const;

export interface TopNavProps {
  /** When provided, renders a hamburger button on the left that calls this on tap. Set on mobile only. */
  readonly onMenuClick?: () => void;
}

export function TopNav({ onMenuClick }: TopNavProps = {}): JSX.Element {
  const { isManager, isProgrammer } = useAuth();
  const { currentMode, availableModes, setMode } = useMode();

  const tabs = isManager
    ? [...BASE_TABS, { label: 'Team', to: '/settings/team' } as const]
    : BASE_TABS;

  return (
    <header
      className="fixed inset-x-0 top-0 z-30 flex items-end justify-between border-b border-border"
      style={{
        backgroundColor: 'rgba(11, 16, 23, 0.95)',
        backdropFilter: 'blur(8px)',
        paddingTop: 'env(safe-area-inset-top)',
        height: 'calc(3.5rem + env(safe-area-inset-top))',
        paddingLeft: 'max(env(safe-area-inset-left), 1rem)',
        paddingRight: 'max(env(safe-area-inset-right), 1rem)',
        paddingBottom: '0',
      }}
    >
      <div className="flex h-14 items-center gap-2 sm:gap-3">
        {onMenuClick && (
          <button
            type="button"
            onClick={onMenuClick}
            aria-label="Open navigation"
            className="touch-target -ml-2 flex h-11 w-11 items-center justify-center text-text-dim hover:text-text rounded-md md:hidden"
          >
            <HamburgerIcon />
          </button>
        )}
        <Wordmark />
      </div>
      <nav className="hidden h-14 md:flex items-center gap-1">
        {tabs.map((tab) => (
          <NavTab key={tab.to} to={tab.to} label={tab.label} />
        ))}
      </nav>
      <div className="flex h-14 items-center gap-2 sm:gap-3">
        {/* Mode toggle: visible for programmer (can switch to operator view) */}
        {isProgrammer && availableModes.length > 1 && (
          <div className="hidden md:flex items-center rounded-md border border-border overflow-hidden text-[11px] font-mono">
            {(['programmer', 'operator'] as const).map(mode => (
              <button
                key={mode}
                type="button"
                onClick={() => setMode(mode)}
                className={[
                  'px-2 py-1 capitalize transition-colors',
                  currentMode === mode ? 'bg-card text-text' : 'text-text-dim hover:text-text',
                ].join(' ')}
              >
                {mode}
              </button>
            ))}
          </div>
        )}
        <LiveStatus />
        <VoiceAssistant />
        <ShopChip />
        <UserMenu />
      </div>
    </header>
  );
}

function HamburgerIcon(): JSX.Element {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={22}
      height={22}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      aria-hidden="true"
    >
      <path d="M4 6h16M4 12h16M4 18h16" />
    </svg>
  );
}

function Wordmark(): JSX.Element {
  return (
    <Link to="/dashboard" className="font-mono text-base font-semibold tracking-wide select-none">
      <span style={{ color: 'var(--color-accent)' }}>werk</span>
      <span style={{ color: 'var(--color-muted)' }}>OS</span>
    </Link>
  );
}

function NavTab({ to, label }: { readonly to: string; readonly label: string }): JSX.Element {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const isActive = pathname === to || pathname.startsWith(`${to}/`);
  return (
    <Link
      to={to}
      className={[
        'px-3 py-1.5 text-sm rounded-md transition-colors',
        isActive ? 'text-text bg-card' : 'text-text-dim hover:text-text hover:bg-card/40',
      ].join(' ')}
    >
      {label}
    </Link>
  );
}

function LiveStatus(): JSX.Element {
  const { connection } = useLiveData();
  if (connection !== 'connected') {
    return (
      <div className="hidden md:block">
        <ConnectionStatus state={connection} />
      </div>
    );
  }
  return (
    <div className="hidden md:flex items-center gap-2 px-2 py-1 rounded-md border border-border">
      <span
        className="inline-block h-2 w-2 rounded-full"
        style={{
          backgroundColor: 'var(--color-green)',
          boxShadow: '0 0 6px var(--color-green)',
          animation: 'pulse-glow 1.6s ease-in-out infinite',
        }}
      />
      <span className="text-[11px] font-mono text-text-dim">
        Live <span className="text-muted">·</span>{' '}
        <span className="text-text">streaming</span>
      </span>
    </div>
  );
}

function ShopChip(): JSX.Element {
  const { shopId } = useAuth();
  if (!shopId) return <></>;
  return (
    <span
      className="hidden sm:inline-block text-[11px] font-mono px-2 py-1 rounded-md border border-border text-text-dim"
      title={shopId}
    >
      {shopId}
    </span>
  );
}

// ── Shop name (localStorage-backed per user) ──────────────────────────────────

function useShopName(userId: string | null): [string | null, (name: string) => void] {
  const key = userId ? `werkos:shop_name:${userId}` : null;
  const [name, setName] = useState<string | null>(() =>
    key ? window.localStorage.getItem(key) : null,
  );
  const save = (n: string) => {
    if (!key) return;
    window.localStorage.setItem(key, n);
    setName(n);
  };
  return [name, save];
}

function getShopInitials(value: string): string {
  const words = value.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return '?';
  if (words.length === 1) return words[0]!.slice(0, 2).toUpperCase();
  return (words[0]![0]! + words[words.length - 1]![0]!).toUpperCase();
}

// ── User menu ─────────────────────────────────────────────────────────────────

type MenuPage = 'main' | 'edit-profile';

function UserMenu(): JSX.Element {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [shopName, saveShopName] = useShopName(user?.sub ?? null);
  const [open, setOpen] = useState(false);
  const [page, setPage] = useState<MenuPage>('main');
  const [confirming, setConfirming] = useState(false);
  const [nameInput, setNameInput] = useState('');
  const menuRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const initials = getShopInitials(shopName || user?.shopId || '?');

  const openMenu  = () => { setOpen(true);  setPage('main'); setConfirming(false); swipe.reset(); };
  const closeMenu = () => { setOpen(false); setPage('main'); setConfirming(false); };
  const swipe = useSwipeDown(closeMenu);

  const startEditProfile = () => {
    setNameInput(shopName ?? '');
    setPage('edit-profile');
    requestAnimationFrame(() => inputRef.current?.focus());
  };

  const saveProfile = () => {
    const trimmed = nameInput.trim();
    if (trimmed) saveShopName(trimmed);
    setPage('main');
  };

  const handleSignOut = () => {
    if (!confirming) { setConfirming(true); return; }
    void signOut().then(() => window.location.assign('/login'));
  };

  useEffect(() => {
    if (!open) return;
    const onMouse = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) closeMenu();
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') closeMenu(); };
    document.addEventListener('mousedown', onMouse);
    window.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onMouse); window.removeEventListener('keydown', onKey); };
  }, [open]);

  // ── Shared content ───────────────────────────────────────────────────────────

  const editProfileContent = (
    <div className="p-4 space-y-3">
      <button type="button" onClick={() => setPage('main')} className="text-[10px] font-mono text-text-dim hover:text-text flex items-center gap-1">
        ← Back
      </button>
      <p className="text-xs font-mono uppercase tracking-widest text-text-dim">Shop name</p>
      <input
        ref={inputRef}
        type="text"
        value={nameInput}
        onChange={e => setNameInput(e.target.value)}
        onKeyDown={e => { if (e.key === 'Enter') saveProfile(); }}
        placeholder={user?.shopId ?? 'My Shop'}
        maxLength={40}
        className="w-full rounded-md border border-border bg-card px-3 py-2 text-sm font-mono text-text placeholder:text-muted focus:border-accent focus:outline-none"
      />
      <p className="text-[10px] font-mono text-muted">Shown as your avatar initials.</p>
      <div className="flex gap-2 pt-1">
        <button type="button" onClick={saveProfile} className="flex-1 rounded-md bg-accent/15 border border-accent/30 py-2 text-sm font-mono text-accent hover:bg-accent/25 transition-colors">Save</button>
        <button type="button" onClick={() => setPage('main')} className="flex-1 rounded-md border border-border py-2 text-sm font-mono text-text-dim hover:text-text transition-colors">Cancel</button>
      </div>
    </div>
  );

  const mainContent = (
    <>
      <div className="px-4 py-3 border-b border-border">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-full border border-border bg-card text-sm font-mono text-text flex items-center justify-center shrink-0">
            {initials}
          </div>
          <div className="min-w-0">
            <p className="text-sm font-mono text-text truncate">{shopName || user?.shopId || '—'}</p>
            <p className="text-[10px] font-mono text-text-dim truncate">{user?.email ?? ''}</p>
          </div>
        </div>
      </div>

      <div className="py-1">
        <MenuItem icon="✎" label="Edit Profile" onClick={startEditProfile} />
        <MenuItem icon="⊙" label="Change Photo"       onClick={closeMenu} soon />
        <MenuItem icon="⚙" label="Settings"            onClick={() => { closeMenu(); void navigate({ to: '/settings' }); }} />
        <MenuItem icon="⌨" label="Keyboard Shortcuts"  onClick={closeMenu} soon />
        <MenuItem icon="★" label="What's New"          onClick={closeMenu} soon />
      </div>

      <div className="border-t border-border py-1">
        <button
          type="button"
          onClick={handleSignOut}
          className={[
            'w-full flex items-center gap-3 px-4 py-2.5 text-left text-sm font-mono transition-colors',
            confirming ? 'text-red hover:bg-red/10' : 'text-text-dim hover:text-red hover:bg-card/60',
          ].join(' ')}
        >
          <span className="text-xs w-4 text-center opacity-60">→</span>
          {confirming ? 'Tap again to sign out' : 'Sign Out'}
        </button>
      </div>
    </>
  );

  const content = page === 'edit-profile' ? editProfileContent : mainContent;

  return (
    <div ref={menuRef} className="relative">
      <button
        type="button"
        onClick={open ? closeMenu : openMenu}
        aria-label="User menu"
        aria-expanded={open}
        className="touch-target h-9 w-9 rounded-full border border-border bg-card text-xs font-mono text-text hover:border-accent transition-colors flex items-center justify-center"
      >
        {initials}
      </button>

      {/* Desktop dropdown */}
      {open && (
        <div
          className="absolute right-0 top-full mt-2 w-64 rounded-xl border border-border shadow-2xl overflow-hidden hidden md:block"
          style={{ backgroundColor: 'rgba(11,16,23,0.97)', backdropFilter: 'blur(8px)', zIndex: 50 }}
        >
          {content}
        </div>
      )}

      {/* Mobile full-screen sheet — portalled to body to escape TopNav's backdropFilter containing block */}
      {open && createPortal(
        <div className="fixed inset-0 z-50 flex flex-col justify-end md:hidden">
          <div className="absolute inset-0 bg-black/60" onClick={closeMenu} />
          <div
            className="relative w-full rounded-t-2xl border-t border-border overflow-hidden"
            style={{
              backgroundColor: 'rgba(11,16,23,0.97)',
              animation: 'sheet-up 0.35s cubic-bezier(0.32, 0.72, 0, 1)',
              paddingBottom: 'env(safe-area-inset-bottom)',
              ...swipe.style,
            }}
          >
            {/* Drag handle — swipe down to dismiss */}
            <div
              className="flex justify-center pt-3 pb-1 touch-none cursor-grab"
              {...swipe.handlers}
            >
              <div className="h-1 w-10 rounded-full bg-border" />
            </div>
            {content}
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
}

function MenuItem({
  icon, label, onClick, soon = false,
}: {
  readonly icon: string;
  readonly label: string;
  readonly onClick: () => void;
  readonly soon?: boolean;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full flex items-center justify-between px-4 py-2.5 text-left text-sm font-mono text-text-dim hover:text-text hover:bg-card/60 transition-colors"
    >
      <span className="flex items-center gap-3">
        <span className="text-xs w-4 text-center opacity-60">{icon}</span>
        {label}
      </span>
      {soon && <span className="text-[9px] font-mono uppercase tracking-widest text-muted">soon</span>}
    </button>
  );
}
